import React from "react";
import { ArrowLeftIcon } from "lucide-react";
import { Button } from "./button";
import { DiffDisplay, useDiffStats } from "./DiffDisplay";

interface DiffViewProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  original: string;
  proposed: string;
}

const DiffView: React.FC<DiffViewProps> = ({
  isOpen,
  onClose,
  title,
  description,
  original,
  proposed
}) => {
  const diffStats = useDiffStats(isOpen ? original : "", isOpen ? proposed : "");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] bg-background flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center justify-between p-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-gc-demi-bold text-foreground truncate">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
          <Button
            onClick={onClose}
            variant="outline"
            className="gap-2 ml-4"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 overflow-hidden">
        <div className="h-full flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground">
              Changes
            </h3>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>{proposed.length} characters</span>
              <span>{proposed.split("\n").length} lines</span>
            </div>
          </div>
          
          <DiffDisplay 
            original={original} 
            proposed={proposed}
            className="flex-1"
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <span className="inline-block w-3 h-3 bg-green-100 dark:bg-green-900/30 rounded"></span>
              {diffStats.added} added lines
            </span>
            <span className="inline-flex items-center gap-2 ml-4">
              <span className="inline-block w-3 h-3 bg-red-100 dark:bg-red-900/30 rounded"></span>
              {diffStats.removed} removed lines
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiffView;
